import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const RenewalModal = ({ subscription, onClose, onRenewal }) => {
  const [renewalData, setRenewalData] = useState({
    duration: '1',
    durationType: 'month',
    subscriptionType: subscription.subscriptionType,
    startDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const subscriptionTypes = [
    'Basic Monthly',
    'Premium Monthly',
    'Basic Yearly',
    'Premium Yearly',
    'VIP Monthly',
    'VIP Yearly'
  ];

  const handleInputChange = (field, value) => {
    setRenewalData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateNewEndDate = (startDate, duration, durationType) => {
    const start = new Date(startDate);
    const durationNum = parseInt(duration);
    
    if (durationType === 'month') {
      start.setMonth(start.getMonth() + durationNum);
    } else {
      start.setFullYear(start.getFullYear() + durationNum);
    }
    
    return start;
  };

  const handleRenewal = () => {
    console.log('Individual renewal data:', {
      client: subscription,
      renewalData
    });
    
    // Mock renewal process
    onRenewal();
  };

  const newEndDate = calculateNewEndDate(
    renewalData.startDate,
    renewalData.duration,
    renewalData.durationType
  );

  const getStatusColor = (status) => {
    return status === 'ending-soon' ? 'text-yellow-600' : 'text-red-600';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Renew Subscription</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Client Info */}
          <div className="flex items-center mb-6 p-4 bg-gray-50 rounded-lg">
            <Image
              src={subscription.avatar}
              alt={subscription.clientName}
              className="w-12 h-12 rounded-full mr-4"
            />
            <div className="flex-1">
              <h4 className="text-lg font-medium text-gray-900">
                {subscription.clientName}
              </h4>
              <p className="text-sm text-gray-500">{subscription.phone}</p>
              <div className="flex items-center mt-1">
                <span className="text-sm text-gray-600 mr-2">Current:</span>
                <span className="text-sm font-medium">{subscription.subscriptionType}</span>
              </div>
              <div className="flex items-center mt-1">
                <span className="text-sm text-gray-600 mr-2">Status:</span>
                <span className={`text-sm font-medium ${getStatusColor(subscription.status)}`}>
                  {subscription.status === 'ending-soon' 
                    ? `Ending in ${subscription.daysRemaining} days`
                    : `Expired ${subscription.daysOverdue} days ago`
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Renewal Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Renewal Duration
              </label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={renewalData.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                />
                <select
                  value={renewalData.durationType}
                  onChange={(e) => handleInputChange('durationType', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                >
                  <option value="month">Month(s)</option>
                  <option value="year">Year(s)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Type
              </label>
              <select
                value={renewalData.subscriptionType}
                onChange={(e) => handleInputChange('subscriptionType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                {subscriptionTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Renewal Start Date
              </label>
              <input
                type="date"
                value={renewalData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={renewalData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
                placeholder="Add any notes about this renewal..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Preview */}
            <div className="bg-green-50 rounded-lg p-4">
              <h5 className="text-sm font-medium text-green-900 mb-2">Renewal Summary</h5>
              <div className="text-sm text-green-800 space-y-1">
                <p>Duration: {renewalData.duration} {renewalData.durationType}(s)</p>
                <p>New end date: {newEndDate.toLocaleDateString()}</p>
                <p>Subscription: {renewalData.subscriptionType}</p>
                <p>Start date: {new Date(renewalData.startDate).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleRenewal}
            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700"
          >
            <Icon name="RefreshCw" size={16} className="mr-2 inline" />
            Renew Subscription
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenewalModal;